import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import { plants as plantsApi, geocode as geocodeApi } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { idNative, idEndangered, idStateNote } from "@/lib/idCompat";
import { sharePlant } from "@/lib/sharePlant";
import { normalizeSunPreference } from "@/lib/sunPref";
import PhotoEditor from "@/components/PhotoEditor";
import { compressImage, readFileAsDataURL } from "@/lib/imageCompress";
import { Lightbox } from "@/components/result/Lightbox";
import LocationTagDialog from "@/components/result/LocationTagDialog";
import FirstScanNudge from "@/components/result/FirstScanNudge";
import { PhotoCarousel } from "@/components/result/PhotoCarousel";
import { PhotoThumbStrip } from "@/components/result/PhotoThumbStrip";
import { JournalCard } from "@/components/result/JournalCard";
import ResultDialogs from "@/components/result/ResultDialogs";
import CorridorContributeCard from "@/components/result/CorridorContributeCard";
import ResultToolbar from "@/components/result/ResultToolbar";
import PhotoActionRow from "@/components/result/PhotoActionRow";
import ResultHeader from "@/components/result/ResultHeader";
import IdentificationCards from "@/components/result/IdentificationCards";
import TagsSection from "@/components/result/TagsSection";
import { useMilestonesContext } from "@/contexts/MilestonesContext";

// Undo Delete — misclick heuristic constants.
// A trash tap fired within MISCLICK_WINDOW_MS of the detail page
// mounting is likely a misclick (user was aiming for Replace / Add
// photo, both adjacent). We lengthen the Undo toast from the default
// duration to UNDO_DURATION_MISCLICK_MS so the user has generous time
// to hit Undo. Server TTL is 60s, so 15s stays well inside the window.
const MISCLICK_WINDOW_MS = 2_000;
const UNDO_DURATION_DEFAULT_MS = 5_000;
const UNDO_DURATION_MISCLICK_MS = 15_000;

/**
 * Result — slim orchestrator. All state and action handlers live here
 * (deeply intertwined with `setScan`, `setTranslatedId`, `setAlts`
 * resets across many callbacks), but every JSX block is now a
 * self-contained subcomponent in `@/components/result/*` that takes
 * props for what to render + callbacks for what to do.
 *
 * Sub-sections (top → bottom):
 *   • ResultToolbar    — back + replace-photo
 *   • PhotoCarousel    — primary + extras with tap-to-lightbox
 *   • PhotoThumbStrip  — jump-to-slide row on multi-photo scans
 *   • ResultHeader     — title/share/location/date/badges/draft/retry
 *   • IdentificationCards — sun/dormancy/companion/keystone/status/…
 *   • TagsSection      — tags card
 *   • JournalCard      — dated notes timeline
 *   • Extra-photo strip + view-gallery button + CorridorContributeCard
 *   • Dialogs: rename/retry/photo-editor/lightbox/location-tag + FirstScanNudge
 */
export default function Result() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { checkNow: checkMilestones } = useMilestonesContext();
  const initialScan = state?.scan;
  const freshlyIdentified = state?.freshlyIdentified === true;

  const [scan, setScan] = useState(initialScan);
  const [alts, setAlts] = useState(null);
  const [altsLoading, setAltsLoading] = useState(false);
  const [translatedId, setTranslatedId] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameText, setRenameText] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [sharing, setSharing] = useState(false);
  // Location tag dialog — opens on "Tag location" click and offers both
  // browser-geolocation (fast path when the user is still at the site)
  // and manual ZIP entry (when reviewing a scan long after leaving).
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [addingPhoto, setAddingPhoto] = useState(false);
  const [placeLabel, setPlaceLabel] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [savingTags, setSavingTags] = useState(false);
  // Date-taken editor (pencil chip beside the location chip).
  const [editingDate, setEditingDate] = useState(false);
  const [dateDraft, setDateDraft] = useState("");
  // User's existing tags across their whole gallery — shown as clickable
  // chips so common tags can be applied without retyping.
  const [knownTags, setKnownTags] = useState([]);
  const [replaceSrc, setReplaceSrc] = useState(null);   // dataURL pending crop
  const [addPhotoSrc, setAddPhotoSrc] = useState(null); // dataURL pending crop (add-extra flow)
  const [replacing, setReplacing] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null); // full-screen photo viewer
  const [regeneratingUses, setRegeneratingUses] = useState(false);
  const [retryConfirmOpen, setRetryConfirmOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [identifyingDraft, setIdentifyingDraft] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingScan, setDeletingScan] = useState(false);
  // Misclick heuristic: if the user tapped the trash icon within 2s of
  // the detail page mounting, they probably meant to tap the adjacent
  // Replace / Add-photo button, so we lengthen the Undo window from
  // 5s → 15s. Both refs so a re-render doesn't reset them.
  const mountedAt = useRef(Date.now());
  const trashTappedAt = useRef(0);
  const carouselRef = useRef(null);

  const refreshKnownTags = async () => {
    try {
      const { data } = await plantsApi.tags();
      setKnownTags((data?.items || []).map((r) => r.tag));
    } catch { /* non-critical */ }
  };
  useEffect(() => { refreshKnownTags(); }, []);

  // Keep scan in sync if a new one arrives via navigation
  useEffect(() => {
    if (initialScan && initialScan?.scan_id !== scan?.scan_id) setScan(initialScan);
  }, [initialScan, scan?.scan_id]);

  // Trigger the milestone catalog check the first time a freshly-identified
  // scan lands here. Fires only for the Camera → Result hop (guarded by
  // `freshlyIdentified`) so browsing existing gallery cards never
  // re-triggers a celebration. Backend endpoint is idempotent.
  useEffect(() => {
    if (!freshlyIdentified || !initialScan?.scan_id) return;
    checkMilestones();
  }, [freshlyIdentified, initialScan?.scan_id, checkMilestones]);

  // Hydrate the full-resolution photos on mount. The Gallery list
  // endpoint strips `image_base64` and `extra_images` for performance,
  // so a scan arriving via router state only carries `thumb_base64`.
  // Also refetch on browser reload — router state can be stale (e.g.
  // after a reorder-then-refresh, history.state.usr still has the
  // OLD extras order until we replace it). The one case where we can
  // trust the router state is a freshly-identified scan (Camera →
  // Result hop), because it hasn't touched the server since.
  useEffect(() => {
    if (!scan?.scan_id) return;
    if (freshlyIdentified && scan.image_base64 && ("extra_images" in scan)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await plantsApi.get(scan.scan_id);
        if (!cancelled && data) setScan((prev) => ({ ...(prev || {}), ...data }));
      } catch { /* card still renders using thumb_base64 as a fallback */ }
    })();
    return () => { cancelled = true; };
    // Only refetch when the id itself changes; keep scan-field deps
    // out to avoid the setScan merge triggering an infinite loop.
  }, [scan?.scan_id, freshlyIdentified]);

  // Auto-translate when user.language differs from scan.language
  useEffect(() => {
    if (!scan?.scan_id) return;
    setTranslatedId(null);
    const userLang = user?.language || "en";
    const scanLang = scan.language || "en";
    if (userLang === scanLang) return;
    const cached = scan.translations?.[userLang];
    if (cached) { setTranslatedId(cached); return; }
    let cancelled = false;
    setTranslating(true);
    plantsApi.translate(scan.scan_id, userLang)
      .then(({ data }) => { if (!cancelled) setTranslatedId(data.identification); })
      .catch(() => { /* fall back to original */ })
      .finally(() => { if (!cancelled) setTranslating(false); });
    return () => { cancelled = true; };
  }, [scan?.scan_id, scan?.language, scan?.translations, user?.language]);

  // Reverse geocode the location chip whenever it changes
  useEffect(() => {
    let cancelled = false;
    if (scan?.location?.lat != null && scan?.location?.lng != null) {
      setPlaceLabel(null);
      geocodeApi.reverse(scan.location.lat, scan.location.lng)
        .then(({ data }) => { if (!cancelled) setPlaceLabel(data?.label || null); })
        .catch(() => { if (!cancelled) setPlaceLabel(null); });
    } else {
      setPlaceLabel(null);
    }
    return () => { cancelled = true; };
  }, [scan?.location?.lat, scan?.location?.lng]);

  // ESC closes the lightbox.
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => { if (e.key === "Escape") setLightboxSrc(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxSrc]);

  // Reset carousel back to slide 0 whenever the scan changes.
  // Also rebase the misclick-heuristic timer so a fast tap on a
  // freshly-navigated detail page still counts as "quick" — the user
  // hasn't actually spent time on THIS scan yet.
  useEffect(() => {
    setCarouselIndex(0);
    if (carouselRef.current) carouselRef.current.scrollLeft = 0;
    mountedAt.current = Date.now();
    trashTappedAt.current = 0;
  }, [scan?.scan_id]);

  // Return to whichever list brought us here. `state.from` is the full
  // URL (path + query) so filters/tags on the Gallery are preserved
  // when the user taps the back arrow.
  const goBack = () => {
    if (state?.from === "duplicates") { navigate("/duplicates"); return; }
    if (state?.from && typeof state.from === "string" && state.from.startsWith("/")) {
      navigate(state.from);
      return;
    }
    navigate("/gallery");
  };

  const onCarouselScroll = () => {
    const el = carouselRef.current;
    if (!el) return;
    const w = el.clientWidth || 1;
    const next = Math.round(el.scrollLeft / w);
    setCarouselIndex((prev) => (prev === next ? prev : next));
  };

  const onIdentifyDraft = async () => {
    setIdentifyingDraft(true);
    try {
      const { data } = await plantsApi.identifyExisting(scan.scan_id);
      setScan(data);
      setTranslatedId(null);
      setAlts(null);
      const newName = (data?.identification?.common_name || "").trim();
      const lower = newName.toLowerCase();
      if (!newName || lower === "unknown" || lower === "desconocido") {
        toast.message(t("result.identifyDraftLow"));
      } else {
        toast.success(t("result.identifyDraftSuccess", { name: newName }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("result.identifyDraftFail"));
    } finally {
      setIdentifyingDraft(false);
    }
  };

  const onConfirmRetry = async () => {
    setRetryConfirmOpen(false);
    setRetrying(true);
    try {
      const { data } = await plantsApi.retryIdentify(scan.scan_id);
      setScan(data);
      setTranslatedId(null);
      setAlts(null);
      const newName = (data?.identification?.common_name || "").trim();
      const lower = newName.toLowerCase();
      if (!newName || lower === "unknown" || lower === "desconocido") {
        toast.message(t("result.retryNoBetterGuess"));
      } else {
        toast.success(t("result.retrySuccess", { name: newName }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("result.retryFail"));
    } finally {
      setRetrying(false);
    }
  };

  if (!scan) {
    return (
      <AppShell>
        <div className="px-5 py-10 text-center text-leaf-mist">
          {t("result.noResult")}
          <div className="mt-4">
            <button onClick={() => navigate("/camera")} className="text-leaf underline">
              {t("result.takePhoto")}
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  const scanState = scan.state || user?.state || "";
  const rawId = translatedId || scan.identification;
  // Defensive normalization: LLMs occasionally return strings where the
  // schema expects arrays. Coerce so downstream .map/.length is safe.
  const toArray = (v) => Array.isArray(v) ? v : (v == null || v === "" ? [] : [String(v)]);
  const id = {
    ...rawId,
    sun_preference: normalizeSunPreference(rawId?.sun_preference),
    keystone_species_support: toArray(rawId?.keystone_species_support),
  };
  const isNative = idNative(id);
  const isEndangered = idEndangered(id);
  const stateNote = idStateNote(id);
  const plantType = (id.plant_type || "").toLowerCase();
  const showAlts = isNative === false || plantType === "weed";
  const confLabel = id.confidence
    ? t(`result.confidenceLevels.${id.confidence}`, { defaultValue: id.confidence })
    : "";

  // Date-taken editor helpers
  const openDateEditor = () => {
    const seed = (scan.date_taken || "").slice(0, 10);
    setDateDraft(seed);
    setEditingDate(true);
  };
  const saveDateTaken = async (explicitValue) => {
    // Accept an explicit value from callers that need to bypass the
    // state draft (notably "clear date", which would race the setter
    // and re-save the old date via the stale closure).
    const val = (explicitValue !== undefined ? explicitValue : dateDraft || "").trim();
    if (val && val > new Date().toISOString().slice(0, 10)) {
      toast.error(t("result.dateTakenFuture"));
      return;
    }
    try {
      const { data } = await plantsApi.setDateTaken(scan.scan_id, val || null);
      setScan(data);
      setEditingDate(false);
      toast.success(val ? t("result.dateTakenSaved") : t("result.dateTakenCleared"));
    } catch {
      toast.error(t("result.dateTakenFail"));
    }
  };

  const loadAlternatives = async () => {
    setAltsLoading(true);
    try {
      const { data } = await plantsApi.alternatives(id.common_name, id.scientific_name, id.plant_type);
      setAlts(data.alternatives || []);
    } catch {
      toast.error(t("result.altsLoadFail"));
    } finally {
      setAltsLoading(false);
    }
  };

  const submitRename = async () => {
    const name = renameText.trim();
    if (!name) return;
    setRenaming(true);
    try {
      const { data } = await plantsApi.rename(scan.scan_id, name);
      setScan(data);
      setTranslatedId(null);
      setAlts(null);
      toast.success(t("result.renameSuccess"));
      setRenameOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("result.renameFail"));
    } finally {
      setRenaming(false);
    }
  };

  const onShare = async () => {
    setSharing(true);
    try {
      const res = await sharePlant({ scan, id, state: scanState, t });
      if (res?.mode === "clipboard") toast.success(t("share.clipboardOk"));
      else if (res?.mode === "download") toast.success(t("share.downloadOk"));
      else if (!res?.ok && res?.mode !== "abort") toast.error(t("share.fail"));
    } catch {
      toast.error(t("share.fail"));
    } finally {
      setSharing(false);
    }
  };

  const onTagLocation = () => setLocationDialogOpen(true);

  const onClearLocation = async () => {
    try {
      const { data } = await plantsApi.setLocation(scan.scan_id, null);
      setScan(data);
      toast.success(t("result.locationCleared"));
    } catch {
      toast.error(t("result.locationFail"));
    }
  };

  const onPickExtraPhoto = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error(t("camera.toastBadType")); return; }
    // Show the same crop/rotate editor Replace uses so the user can
    // frame the shot before it's committed — otherwise a slightly-off
    // capture (a common phone-camera failure mode) is stuck on the
    // scan forever. Compression + upload happens in the confirm
    // handler once the user has finalised the crop.
    const dataUrl = await readFileAsDataURL(file);
    setAddPhotoSrc(dataUrl);
  };

  const onConfirmAddPhoto = async (edited) => {
    setAddPhotoSrc(null);
    setAddingPhoto(true);
    try {
      // Shared byte-budget compressor keeps every extra ≤ ~900KB base64
      // so the plant_scans doc never approaches Mongo's 16 MB BSON limit.
      const compact = await compressImage(edited, { profile: "photo" });
      const { data } = await plantsApi.addPhoto(scan.scan_id, compact);
      setScan(data);
      toast.success(t("result.photoAdded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("result.photoAddFail"));
    } finally {
      setAddingPhoto(false);
    }
  };

  const onRemoveExtra = async (photoId) => {
    try {
      const { data } = await plantsApi.removePhoto(scan.scan_id, photoId);
      setScan(data);
    } catch {
      toast.error(t("result.photoRemoveFail"));
    }
  };

  // Optimistic reorder of extra photos. Updates local state
  // immediately so the visual reorder feels instant, then PATCHes the
  // server; on failure we snap back to the previous order and toast.
  const onReorderExtras = async (newPhotoIds) => {
    const prevExtras = scan.extra_images || [];
    const byId = new Map(prevExtras.map((p) => [p.photo_id, p]));
    const nextExtras = newPhotoIds.map((id) => byId.get(id)).filter(Boolean);
    // Guard: if the client sent a mismatched set (shouldn't happen —
    // strip only emits permutations), bail without touching state.
    if (nextExtras.length !== prevExtras.length) return;
    setScan((prev) => ({ ...(prev || {}), extra_images: nextExtras }));
    try {
      const { data } = await plantsApi.reorderPhotos(scan.scan_id, newPhotoIds);
      setScan(data);
    } catch {
      // Revert the optimistic update on API failure.
      setScan((prev) => ({ ...(prev || {}), extra_images: prevExtras }));
      toast.error(t("result.photoReorderFail"));
    }
  };

  // Delete the whole scan. Confirmed via `deleteConfirmOpen` dialog so
  // an accidental tap on the new red trash affordance never removes a
  // plant silently. On success, the backend returns `snapshot_id` +
  // `undo_expires_in_seconds` so we can offer a 5-second Undo action on
  // the success toast — same pattern the Gallery already uses.
  const onConfirmDeleteScan = async () => {
    if (deletingScan || !scan?.scan_id) return;
    setDeletingScan(true);
    const scanId = scan.scan_id;
    try {
      const { data } = await plantsApi.remove(scanId);
      setDeleteConfirmOpen(false);
      const snapshotId = data?.snapshot_id;
      // Navigate to Gallery first so the user is out of the now-stale
      // detail view; the toast follows them there.
      navigate("/gallery");
      if (snapshotId) {
        // Compute the Undo window. If the trash was tapped very soon
        // after arriving on this scan, it was likely a misclick, so
        // give the user a longer runway to hit Undo.
        const tappedAt = trashTappedAt.current || Date.now();
        const timeOnPage = tappedAt - mountedAt.current;
        const likelyMisclick = timeOnPage < MISCLICK_WINDOW_MS;
        const undoDuration = likelyMisclick
          ? UNDO_DURATION_MISCLICK_MS
          : UNDO_DURATION_DEFAULT_MS;
        const label = likelyMisclick
          ? t("result.scanDeletedMisclickHint")
          : t("result.scanDeleted");
        toast.success(label, {
          duration: undoDuration,
          // Bottom-center avoids stacking under Gallery's persistent
          // "long-press to delete" coach-mark toast (top-center), which
          // would otherwise intercept pointer events on our Undo button.
          position: "bottom-center",
          action: {
            label: t("common.undo"),
            onClick: async () => {
              try {
                await plantsApi.undo(snapshotId);
                toast.success(t("result.scanDeleteUndone"));
                // Return the user to the just-restored scan so they
                // immediately see the outcome instead of hunting for
                // it in the gallery list.
                navigate(`/plant/${scanId}`);
              } catch (e) {
                const detail = e?.response?.data?.detail;
                toast.error(
                  detail === "undo_expired"
                    ? t("result.scanUndoExpired")
                    : t("result.scanUndoFail")
                );
              }
            },
          },
        });
      } else {
        toast.success(t("result.scanDeleted"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("result.scanDeleteFail"));
      setDeletingScan(false);
    }
  };

  const onSetPrimary = async (photoId) => {
    try {
      const { data } = await plantsApi.promotePhoto(scan.scan_id, photoId);
      setScan(data);
      // Scroll the carousel back to the (new) primary so the user
      // visually confirms the swap took effect — otherwise they're
      // left staring at the ex-primary in the same slot.
      if (carouselRef.current) {
        carouselRef.current.scrollTo({ left: 0, behavior: "smooth" });
      }
      toast.success(t("result.setPrimaryOk"));
    } catch {
      toast.error(t("result.setPrimaryFail"));
    }
  };

  const onRegenerateUses = async () => {
    if (regeneratingUses || !scan?.scan_id) return;
    setRegeneratingUses(true);
    const toastId = toast.loading(t("result.usesRegenerating"));
    try {
      const { data } = await plantsApi.regenerateUses(scan.scan_id);
      setScan(data);
      toast.success(t("result.usesRegenerateOk"), { id: toastId });
    } catch {
      toast.error(t("result.usesRegenerateFail"), { id: toastId });
    } finally {
      setRegeneratingUses(false);
    }
  };

  const onPickReplacement = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error(t("camera.toastBadType")); return; }
    const dataUrl = await readFileAsDataURL(file);
    setReplaceSrc(dataUrl); // opens PhotoEditor
  };

  const onConfirmReplacement = async (edited) => {
    setReplaceSrc(null);
    setReplacing(true);
    try {
      // Shared byte-budget compressor — same ~900KB ceiling as extras.
      const compact = await compressImage(edited, { profile: "photo" });
      const { data } = await plantsApi.replacePhoto(scan.scan_id, compact, true);
      setScan(data);
      setTranslatedId(null);
      setAlts(null);
      toast.success(t("result.replaceSuccess"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("result.replaceFail"));
    } finally {
      setReplacing(false);
    }
  };

  // ---- Tags -------------------------------------------------------------
  // Persist a new tags list to the server, updating local scan + refreshing
  // the known-tags suggestion pool.
  const saveTags = async (nextTags) => {
    setSavingTags(true);
    try {
      const { data } = await plantsApi.setTags(scan.scan_id, nextTags);
      setScan(data);
      refreshKnownTags();
      return true;
    } catch {
      toast.error(t("result.tagsSaveFail"));
      return false;
    } finally {
      setSavingTags(false);
    }
  };

  const submitTagAdd = async (e) => {
    e?.preventDefault?.();
    const raw = tagInput.trim();
    if (!raw) return;
    if (raw.length > 30) { toast.error(t("result.tagTooLong")); return; }
    const merged = [...(scan.tags || []), raw];
    const dedupedCount = Array.from(new Set(merged.map((s) => s.toLowerCase()))).length;
    if (dedupedCount > 10) { toast.error(t("result.tagsLimitReached")); return; }
    const ok = await saveTags(merged);
    if (ok) setTagInput("");
  };

  const applyKnownTag = async (tag) => {
    if (!tag || savingTags) return;
    if ((scan.tags || []).some((x) => x.toLowerCase() === tag.toLowerCase())) return;
    await saveTags([...(scan.tags || []), tag]);
  };

  const removeTag = async (tag) => {
    await saveTags((scan.tags || []).filter((x) => x !== tag));
  };

  return (
    <AppShell>
      <div className="md:max-w-3xl md:mx-auto">
        <ResultToolbar onBack={goBack} />

        <div className="relative mx-5 rounded-3xl overflow-hidden aspect-[5/4] bg-parchment animate-fade-up" data-testid="photo-carousel">
          <PhotoCarousel
            ref={carouselRef}
            primaryPhoto={scan.image_base64 || scan.thumb_base64}
            extraPhotos={scan.extra_images || []}
            altText={id.common_name || ""}
            carouselIndex={carouselIndex}
            onScroll={onCarouselScroll}
            onSlideClick={setLightboxSrc}
            onRemoveExtra={onRemoveExtra}
            onSetPrimary={onSetPrimary}
          />
        </div>

        {/* Thumbnail strip — jump-to-photo affordance for scans with 2+
            shots. Also supports long-press-drag reorder on extras. */}
        <PhotoThumbStrip
          photos={[
            scan.image_base64 || scan.thumb_base64,
            ...(scan.extra_images || []).map((p) => p.image_base64),
          ].filter(Boolean)}
          extras={scan.extra_images || []}
          activeIndex={carouselIndex}
          altBase={id.common_name || ""}
          onSelectIndex={(i) => {
            const el = carouselRef.current;
            if (!el) return;
            el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
          }}
          onReorderExtras={onReorderExtras}
        />

        {/* Photo-mutation affordances — Replace + Delete + Add — sit
            directly under the photo so all three live in one obvious
            place instead of scattered around the page. */}
        <PhotoActionRow
          replacing={replacing}
          addingPhoto={addingPhoto}
          deleting={deletingScan}
          extraCount={(scan.extra_images || []).length}
          onPickReplacement={onPickReplacement}
          onPickExtraPhoto={onPickExtraPhoto}
          onRequestDeleteScan={() => {
            // Record when the trash affordance was tapped so
            // onConfirmDeleteScan can lengthen the Undo window if
            // this was likely a misclick (see MISCLICK_THRESHOLD_MS).
            trashTappedAt.current = Date.now();
            setDeleteConfirmOpen(true);
          }}
        />

        <ResultHeader
          scan={scan}
          id={id}
          scanState={scanState}
          isNative={isNative}
          isEndangered={isEndangered}
          plantType={plantType}
          confLabel={confLabel}
          translating={translating}
          placeLabel={placeLabel}
          editingDate={editingDate}
          dateDraft={dateDraft}
          onDateDraftChange={setDateDraft}
          identifyingDraft={identifyingDraft}
          retrying={retrying}
          sharing={sharing}
          onOpenRename={() => { setRenameText(id.common_name || ""); setRenameOpen(true); }}
          onShare={onShare}
          onTagLocation={onTagLocation}
          onClearLocation={onClearLocation}
          onOpenDateEditor={openDateEditor}
          onCancelDateEditor={() => setEditingDate(false)}
          onSaveDate={saveDateTaken}
          onClearDate={() => { setDateDraft(""); saveDateTaken(""); }}
          onIdentifyDraft={onIdentifyDraft}
          onRetryIdentify={() => setRetryConfirmOpen(true)}
        />

        <IdentificationCards
          id={id}
          scan={scan}
          scanState={scanState}
          isEndangered={isEndangered}
          stateNote={stateNote}
          plantType={plantType}
          showAlts={showAlts}
          alts={alts}
          altsLoading={altsLoading}
          onLoadAlternatives={loadAlternatives}
          onRegenerateUses={onRegenerateUses}
          regeneratingUses={regeneratingUses}
        />

        <div className="px-5 mt-6 pb-2">
          <TagsSection
            tags={scan.tags || []}
            knownTags={knownTags}
            tagInput={tagInput}
            onTagInputChange={setTagInput}
            savingTags={savingTags}
            onSubmitAdd={submitTagAdd}
            onApplyKnown={applyKnownTag}
            onRemoveTag={removeTag}
          />

          {/* Plant-care journal — dated free-text notes ("moved to shade",
              "first bloom"). Rendered as a small newest-first timeline. */}
          {scan?.scan_id && (
            <JournalCard
              scanId={scan.scan_id}
              journal={scan.journal || []}
              onAdd={(entry) => setScan((prev) => ({
                ...(prev || {}),
                journal: [...(prev?.journal || []), entry],
              }))}
              onDelete={(entryId) => setScan((prev) => ({
                ...(prev || {}),
                journal: (prev?.journal || []).filter((e) => e.entry_id !== entryId),
              }))}
              onUpdate={(entryId, patch) => setScan((prev) => ({
                ...(prev || {}),
                journal: (prev?.journal || []).map((e) =>
                  e.entry_id === entryId ? { ...e, ...patch } : e
                ),
              }))}
            />
          )}

          {/* Compact add-photo affordance lives directly under the
              photo (see <PhotoActionRow /> above) so users don't have
              to scroll to the bottom of the page to add a reference shot. */}

          <button
            onClick={() => navigate("/gallery")}
            data-testid="view-gallery-button"
            className="w-full rounded-full py-4 bg-parchment text-leaf-ink font-medium hover:bg-[#EEE9DE] transition active:scale-[0.98]"
          >
            {t("result.savedTo")}
          </button>

          {/* Corridor opt-in lives at the very bottom — it benefits the
              community more than the individual, so we surface all of
              the identify-value FIRST and only invite the user to share
              once they've clearly finished with the scan. */}
          {id.common_name && (
            <div className="mt-6">
              <CorridorContributeCard scan={scan} onGrantLocation={onTagLocation} />
            </div>
          )}
        </div>
      </div>

      <ResultDialogs
        renameOpen={renameOpen}
        onRenameOpenChange={setRenameOpen}
        renameText={renameText}
        onRenameTextChange={setRenameText}
        renaming={renaming}
        onSubmitRename={submitRename}
        retryConfirmOpen={retryConfirmOpen}
        onRetryConfirmOpenChange={setRetryConfirmOpen}
        retrying={retrying}
        onConfirmRetry={onConfirmRetry}
        retryPlantName={id.common_name}
        deleteConfirmOpen={deleteConfirmOpen}
        onDeleteConfirmOpenChange={setDeleteConfirmOpen}
        deleting={deletingScan}
        onConfirmDelete={onConfirmDeleteScan}
        deletePlantName={id.common_name}
      />

      <PhotoEditor
        open={!!replaceSrc}
        image={replaceSrc}
        onCancel={() => setReplaceSrc(null)}
        onConfirm={onConfirmReplacement}
      />

      <PhotoEditor
        open={!!addPhotoSrc}
        image={addPhotoSrc}
        onCancel={() => setAddPhotoSrc(null)}
        onConfirm={onConfirmAddPhoto}
      />

      {/* Assemble the photo list for the lightbox in the SAME order as
          the carousel — primary first, then extras — so prev/next
          arrows and keyboard nav walk in the expected visual order. */}
      <Lightbox
        src={lightboxSrc}
        alt={id.common_name || ""}
        onClose={() => setLightboxSrc(null)}
        photos={[
          scan.image_base64 || scan.thumb_base64,
          ...(scan.extra_images || []).map((p) => p.image_base64),
        ].filter(Boolean)}
        onChange={setLightboxSrc}
      />

      <LocationTagDialog
        open={locationDialogOpen}
        onOpenChange={setLocationDialogOpen}
        scanId={scan?.scan_id}
        onSaved={setScan}
      />

      {/* First-scan nudge — one-time coach-mark that points at the
          bottom Corridor toggle. Gated by `id.common_name` so the
          corridor card is actually mounted before we anchor to it. */}
      <FirstScanNudge enabled={!!id.common_name} />
    </AppShell>
  );
}
